import { TFunction } from "i18next";
import { Report, ReportScheduled } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { buildScheduleSummary } from "@/components/global/Chat/components/ScheduleModal/buildScheduleSummary";
import { ScheduleData } from "@/components/global/Chat/components/ScheduleModal/types";
import { TextVariant } from "@/components/ui/text";

/** Width of the leading icon column, shared by both report tables. */
export const REPORT_ICON_COLUMN_SIZE = 60;

/**
 * The leading icon on a report row. It does not vary by display type — a chart
 * and a grid are both reports — but a scheduled report gets its own, as the
 * design marks it.
 */
export const ReportIcon: React.FC<{ isScheduled?: boolean }> = ({
  isScheduled = false
}) => (
  <IconContainer size="5" color="secondary">
    {isScheduled ? <ReportScheduled /> : <Report />}
  </IconContainer>
);

interface TruncatedTextCellProps {
  children: React.ReactNode;
  variant?: TextVariant;
  /**
   * "span" where the cell sits inside a `<button>`, whose content model takes
   * phrasing content only and so excludes a `<div>`. Either element renders
   * block-level, so truncation and the overflow measure behave the same.
   */
  as?: "div" | "span";
}

/**
 * The one truncate-with-tooltip text cell the report and schedule tables share.
 * Carries no column or row semantics on purpose — it is the text presentation
 * only, so tables that deliberately differ in their columns can still use it.
 */
export const TruncatedTextCell: React.FC<TruncatedTextCellProps> = ({
  children,
  variant,
  as = "div"
}) => (
  <Text as={as} variant={variant} className="block truncate" hasTruncatedTooltip>
    {children}
  </Text>
);

interface ScheduleSummaryCellProps {
  t: TFunction;
  scheduleConfig: ScheduleData | null | undefined;
  /** Shown when there is no schedule; a table of schedules never needs it. */
  emptyText?: string;
  variant?: TextVariant;
}

/** A schedule read back in words — "Every Monday at 09:00" and the like. */
export const ScheduleSummaryCell: React.FC<ScheduleSummaryCellProps> = ({
  t,
  scheduleConfig,
  emptyText,
  variant
}) => (
  <TruncatedTextCell variant={variant}>
    {scheduleConfig
      ? buildScheduleSummary(t, scheduleConfig, "recurring")
      : emptyText}
  </TruncatedTextCell>
);
